// @LTG-ID: data/journals/selling-a-dental-practice
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 32 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-dental-practice",
    FRONTMATTER: {
        number: "69",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Dental Practice: Valuation Dynamics, DSOs, and Doctor Transitions",
        deck: "Dental practices sell through two main channels: individual dentist buyers and dental service organizations.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-20",
        publishedHuman: "June 20, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 522,
        timeRequired: "PT3M",
        keywords: "selling a dental practice, dental practice valuation, DSO buying practices, dental practice transition",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Dental practices sell through two main channels: individual dentist buyers and dental service organizations. Value rests on active patient count, hygiene recall strength, provider capacity beyond the selling doctor, and payer mix. DSO deals often pay more but typically require the dentist to keep practicing post sale, frequently with equity components. A dental practice is one of the most reliably sellable small businesses in America, and one where the seller's biggest decision is not price. It ",
        complianceNote: "This article is educational and does not constitute legal, financial, or clinical practice advice. Practice transitions involve state dental board and healthcare regulatory considerations requiring specialized counsel.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "owner-dependence-business-value",
        "recurring-revenue-business-value",
        "personal-readiness-to-exit",
        "earnout-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Dental Practice: DSOs & Doctor Transitions · LTG",
        canonical: "https://letstalkgrowth.com/journals/selling-a-dental-practice",
        ogImage: "/journals/og/selling-a-dental-practice.jpg",
    },
    BODY_MARKDOWN: `Dental practices sell through two main channels: individual dentist buyers and dental service organizations. Value rests on active patient count, hygiene recall strength, provider capacity beyond the selling doctor, and payer mix. DSO deals often pay more but typically require the dentist to keep practicing post-sale, frequently with equity components.

A dental practice is one of the most reliably sellable small businesses in America, and one where the seller's biggest decision is not price. It is which of two very different doors to walk through.

## Door one: the individual buyer

The traditional transition sells the practice to another dentist, often a younger doctor buying their first practice, financed heavily by lenders who have long treated dental acquisitions as among the safest small business loans. These deals tend to be clean: the seller transitions patients and referral relationships over a defined period, then exits. The practice's identity continues largely intact.

## Door two: the DSO

Dental service organizations, many backed by private equity, have consolidated a growing share of U.S. dentistry, a shift documented extensively by the American Dental Association and the dental trade press. DSO offers often exceed what an individual buyer can finance, but the structure differs fundamentally: the selling dentist is usually required to continue practicing for a period of years, part of the price frequently arrives as equity in the DSO or its holding company rather than cash, and clinical autonomy questions deserve honest diligence in both directions. A DSO sale is less an exit than a recapitalization with an employment agreement attached, and dentists should evaluate it exactly that way, with the walk-away math from this series applied to the guaranteed cash portion.

## What drives value through either door

- **The active patient base and recall engine.** Buyers ask early: how many active patients, and how strong is hygiene recall? A disciplined recall program is dentistry's version of recurring revenue, measurable, durable, and transferable.
- **Production beyond the seller.** A practice where associates and hygiene generate substantial production transfers far better than one where the selling doctor produces nearly everything. This is owner dependence in clinical form, and it shapes both price and how long the seller must stay.
- **Payer mix and fee reality.** The insurance participation profile, and how much of production comes at what reimbursement, is scrutinized closely, as is compliance hygiene generally.
- **Facility, equipment, and the lease.** Modern operatories and, critically, a transferable lease with adequate term. A practice that cannot stay in its location has a valuation problem no other driver fixes.
- **Staff continuity.** Patients bond with hygienists and front desk as much as with the doctor; team retention is patient retention.

## The dentist-specific preparation list

Two to three years out: build recall discipline and measure it, develop associate capacity if the practice can support it, confirm the lease and its assignability, and organize the practice's numbers to the standard any of this Journal's diligence articles describe. Then decide which door fits your life, not just your price, because the DSO path in particular sells years of your future clinical time, and that belongs in the personal-readiness analysis, not just the financial one.`,
    FAQ: [
        { question: "Do DSOs pay more than individual dentist buyers?", answer: "Often the headline is higher, but structure differs: continued practice obligations, equity components with their own risk, and earnout-like contingencies. Compare offers on guaranteed after-tax cash and required years of work, not the headline." },
        { question: "What makes a dental practice more valuable?", answer: "A large active patient base with strong hygiene recall, production capacity beyond the selling doctor, a favorable payer mix, modern facilities, and a transferable lease. Team stability supports all of it." },
        { question: "How long must I keep working after selling to a DSO?", answer: "Multi-year commitments are typical, with the length negotiated in the transaction documents. The obligation is central to the deal's economics, which is why dentists should treat a DSO sale as a career decision as much as a financial one." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
            meta: "3 min · Brief 45",
            to: "/journals/owner-dependence-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Recurring Revenue: Why Buyers Pay More for Businesses That Bill Every Month",
            meta: "3 min · Brief 49",
            to: "/journals/recurring-revenue-business-value",
        },
        {
            tag: "Personal Readiness",
            title: "Are You Personally Ready to Exit? The Owner's Guide to Financial and Life Readiness",
            meta: "4 min · Brief 58",
            to: "/journals/personal-readiness-to-exit",
        },
    ],
};

export default BRIEF;
